import {useCallback, useEffect, useRef, useState} from 'react';


function easeInSine(x: number): number {
	return x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;
}

export function useCollapse(initialCollapsed = false): [any, boolean, (collapsed: boolean) => void] {
	const [collapsed, setCollapsed] = useState(initialCollapsed);
	const [animationInProcess, setAnimationInProcess] = useState(false);

	const ref = useRef<HTMLElement>();
	const initializedRef = useRef<boolean>(false);
	const heightRef = useRef<number>();
	const collapsePercent = useRef<number>(0);
	const expandTimer = useRef<number>();
	const collapseTimer = useRef<number>();

	const duration = 200;//ms

	const startTimeRef = useRef<number>();

	const calcPercent = useCallback(() => {
		const currentTime = Date.now();
		const passedTime = currentTime - startTimeRef.current;
		return passedTime / duration;
	}, []);

	const expandFunction = useCallback(() => {
		if (!ref.current) {
			return;
		}

		const percent = calcPercent();
		if (percent < 1.0) {
			const h = easeInSine(percent) * heightRef.current;
			ref.current.style.height = `${h}px`;
			ref.current.style.overflow = 'hidden';
			ref.current.style.display = '';
			collapsePercent.current = percent;
			expandTimer.current = requestAnimationFrame(expandFunction);
		} else {
			collapsePercent.current = 1.0;
			ref.current.style.height = '';
			ref.current.style.overflow = '';
			ref.current.style.display = '';
			expandTimer.current = requestAnimationFrame(() => heightRef.current = ref.current.offsetHeight);
			setAnimationInProcess(false);
		}
	}, []);

	const collapseFunction = useCallback(() => {
		const percent = calcPercent();
		if (percent < 1.0) {
			const h = (1 - easeInSine(percent)) * heightRef.current;
			ref.current.style.height = `${h}px`;
			ref.current.style.overflow = 'hidden';
			collapsePercent.current = percent;
			collapseTimer.current = requestAnimationFrame(collapseFunction);
		} else {
			ref.current.style.height = `0px`;
			ref.current.style.overflow = 'hidden';
			ref.current.style.display = 'none';
			collapsePercent.current = 0.0;
			setAnimationInProcess(false);
		}
	}, []);

	const calculate = useCallback((element: HTMLElement) => {
		if (animationInProcess) {
			return;
		}

		if (element) {
			if (!initializedRef.current) {
				heightRef.current = element.offsetHeight;
				if (collapsed) {
					element.style.display = 'none';
					element.style.height = '0px';
					element.style.overflow = 'hidden';
				}
				initializedRef.current = true;
			}
		}
	}, [collapsed, animationInProcess]);

	const toggle = useCallback((value: boolean) => {
		if (animationInProcess) {
			startTimeRef.current = Date.now() - Math.round(duration * collapsePercent.current);
			cancelAnimationFrame(expandTimer.current);
			cancelAnimationFrame(collapseTimer.current);
		}
		setAnimationInProcess(true);
		setCollapsed(value);
		requestAnimationFrame(() => {
			startTimeRef.current = Date.now();
			if (value) {
				collapseFunction();
			} else {
				expandFunction();
			}
		});
	}, [animationInProcess, collapseFunction, expandFunction]);

	useEffect(() => {
		calculate(ref.current);
	}, [calculate, collapsed]);

	return [{
		ref: (r: any) => {
			ref.current = r;
			calculate(r);
		},
	}, collapsed, toggle];
}
